/**
 * PRACTITIONER MEMBERSHIP MANAGEMENT
 *
 * Membership data comes from Supabase.
 * Billing changes and cancellation are handled through Stripe Customer Portal.
 *
 * No Stripe secret keys are exposed to the browser.
 * Every request uses the practitioner's existing Supabase access token.
 */

import {
  integrationConfig,
  isBackendConfigured,
} from './integrationConfig'

import {
  PractitionerApiError,
  clearStoredPractitionerSession,
} from './practitionerApi'

import type {
  MembershipPlanId,
  PractitionerSession,
} from '../types/practitionerSignup'

import type {
  MembershipStatus,
  PractitionerMembership,
} from '../types/membership'

export const membershipIntegration = {
  enabled: true,

  // Existing Fem Entity Edge Functions
  getMembershipFunction: 'manage-practitioner-membership',
  billingPortalFunction: 'create-customer-portal',
} as const

const BILLING_PORTAL_ORIGIN = 'https://billing.stripe.com'

async function callMembershipFunction(
  session: PractitionerSession,
  functionName: string,
  body: Record<string, unknown> = {},
): Promise<{
  status: number
  ok: boolean
  body: Record<string, unknown>
}> {
  if (!membershipIntegration.enabled || !isBackendConfigured()) {
    throw new PractitionerApiError(
      'not_configured',
      'Membership management isn’t connected yet.',
    )
  }

  if (
    !session.accessToken ||
    session.expiresAt - 60_000 <= Date.now()
  ) {
    clearStoredPractitionerSession()

    throw new PractitionerApiError(
      'invalid_credentials',
      'Your session has expired. Please sign in again.',
    )
  }

  let response: Response

  try {
    response = await fetch(
      `${integrationConfig.supabaseUrl}/functions/v1/${functionName}`,
      {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
          apikey: integrationConfig.supabaseAnonKey,
          Authorization: `Bearer ${session.accessToken}`,
        },

        body: JSON.stringify(body),
      },
    )
  } catch {
    throw new PractitionerApiError(
      'network',
      'We couldn’t reach Fem Entity. Check your connection and try again.',
    )
  }

  let json: Record<string, unknown> = {}

  try {
    json = (await response.json()) as Record<string, unknown>
  } catch {
    json = {}
  }

  if (response.status === 401) {
    clearStoredPractitionerSession()

    throw new PractitionerApiError(
      'invalid_credentials',
      'Your session has expired. Please sign in again.',
    )
  }

  return {
    status: response.status,
    ok: response.ok,
    body: json,
  }
}

const STATUSES: MembershipStatus[] = [
  'active',
  'complimentary',
  'cancel_scheduled',
  'past_due',
  'cancelled',
]

const PLAN_IDS: MembershipPlanId[] = [
  'anchor',
  'bloom',
  'thrive',
  'collective',
]

const isoOrNull = (value: unknown): string | null =>
  typeof value === 'string' &&
  !Number.isNaN(new Date(value).getTime())
    ? value
    : null

function parseMembership(raw: unknown): PractitionerMembership {
  const m = (raw ?? {}) as Record<string, unknown>

  if (
    typeof m.status !== 'string' ||
    !STATUSES.includes(m.status as MembershipStatus)
  ) {
    throw new PractitionerApiError(
      'server',
      'We received an incomplete membership response.',
    )
  }

  return {
    planId: PLAN_IDS.includes(m.planId as MembershipPlanId)
      ? (m.planId as MembershipPlanId)
      : null,

    planName:
      typeof m.planName === 'string' && m.planName
        ? m.planName
        : 'Fem Entity membership',

    status: m.status as MembershipStatus,

    monthlyPriceCents:
      typeof m.monthlyPriceCents === 'number'
        ? m.monthlyPriceCents
        : null,

    currency:
      typeof m.currency === 'string' && m.currency
        ? m.currency.toUpperCase()
        : 'CAD',

    nextBillingDate: isoOrNull(m.nextBillingDate),

    currentPeriodEnd: isoOrNull(m.currentPeriodEnd),

    complimentaryEndsAt: isoOrNull(m.complimentaryEndsAt),

    scheduledEndDate: isoOrNull(m.scheduledEndDate),

    isFoundingMember: m.isFoundingMember === true,
  }
}

function failure(
  body: Record<string, unknown>,
  fallback: string,
): never {
  const message =
    typeof body.error === 'string'
      ? body.error
      : typeof body.message === 'string'
        ? body.message
        : fallback

  throw new PractitionerApiError('server', message)
}

/**
 * Sign the practitioner out of the website.
 *
 * Best-effort revokes the session with Supabase Auth's public logout endpoint
 * (publishable key + the practitioner's own token — no privileged credentials),
 * then always clears the locally stored session, even if the network call fails.
 */
export async function signOutPractitioner(
  session: PractitionerSession | null,
): Promise<void> {
  try {
    if (session?.accessToken && isBackendConfigured()) {
      await fetch(`${integrationConfig.supabaseUrl}/auth/v1/logout`, {
        method: 'POST',
        headers: {
          apikey: integrationConfig.supabaseAnonKey,
          Authorization: `Bearer ${session.accessToken}`,
        },
      })
    }
  } catch {
    // Ignore — the local session is cleared below regardless.
  } finally {
    clearStoredPractitionerSession()
  }
}

/**
 * Load the signed-in practitioner's membership.
 *
 * Returns null when the practitioner doesn't have a membership.
 */
export async function getPractitionerMembership(
  session: PractitionerSession,
): Promise<PractitionerMembership | null> {
  const res = await callMembershipFunction(
    session,
    membershipIntegration.getMembershipFunction,
  )

  if (res.status === 404) {
    return null
  }

  if (!res.ok) {
    failure(
      res.body,
      'We couldn’t load your membership right now.',
    )
  }

  return parseMembership(res.body.membership)
}

/**
 * Open Stripe Customer Portal.
 *
 * Stripe handles:
 * - payment methods
 * - billing details
 * - subscription management
 * - membership cancellation
 *
 * The backend creates the Stripe portal session.
 */
export async function openMembershipBillingPortal(
  session: PractitionerSession,
): Promise<void> {
  const res = await callMembershipFunction(
    session,
    membershipIntegration.billingPortalFunction,
  )

  if (!res.ok) {
    failure(
      res.body,
      'We couldn’t open membership settings right now.',
    )
  }

  const url =
    typeof res.body.url === 'string'
      ? res.body.url
      : ''

  if (!url.startsWith(`${BILLING_PORTAL_ORIGIN}/`)) {
    throw new PractitionerApiError(
      'server',
      'We received an unexpected membership settings link.',
    )
  }

  ;(window.top ?? window).location.href = url
}